/**
 * CU-86akbcqv8 — make a Firebase auth code SURVIVE Sentry's default scrubbers.
 *
 * ⚠️ PORTED FROM `mideo-health-app/src/services/mfa/sentryAuthCode.ts`, and
 * deliberately identical: the scrubbing is a property of the SHARED
 * `mideo-unified` Sentry project, not of either client, so a divergent strip
 * rule here would silently produce two spellings of the same code in one issue
 * stream.
 *
 * ── THE PROBLEM (measured 2026-09-03, not inferred) ───────────────────────
 *
 * The project's default scrubbers redact a field when its KEY **or its VALUE**
 * contains a denylisted substring — and `auth` is on that list. Every Firebase
 * Auth code begins with `auth/`, so any code written into `extra`, `contexts`
 * or a breadcrumb arrives as `[Filtered]`.
 *
 * That is how MIDEO-UNIFIED-AF lost its diagnosis on the mobile client: the SMS
 * challenge hook deliberately attached `extra: { code, message }`, with a
 * comment explaining why §3a.6 required it, and BOTH arrived redacted. The
 * instrumentation was correct; the pipeline discarded it. **This portal has the
 * same defect on every `extra: { code }` site.**
 *
 * ⚠️ A breadcrumb is redacted WHOLE — the message line, not merely its `data`.
 *
 * ── WHAT SURVIVES (probe rounds 1-3, MIDEO-UNIFIED-AG/AH/AJ/AK/AM) ────────
 *
 *   | placement            | "auth/foo" | "foo" (stripped) |
 *   |----------------------|------------|------------------|
 *   | tag                  | ✅          | ✅                |
 *   | breadcrumb message   | 🔴 whole    | ✅                |
 *   | breadcrumb data      | 🔴          | ✅                |
 *   | extra / contexts     | 🔴          | ✅                |
 *   | exception value/title| ✅          | ✅                |
 *
 * Tags are exempt from VALUE scrubbing entirely.
 *
 * ── WHY STRIP RATHER THAN EXEMPT ──────────────────────────────────────────
 *
 * A Safe Fields entry would restore the field, but it disables BOTH scrubbing
 * rules for that path — so a genuine credential landing there later would also
 * survive. On an SMS OTP ceremony that is precisely the defence worth keeping.
 * Stripping a four-character prefix keeps every default scrubber intact.
 *
 * ⚠️ NOT a general sanitiser. It removes ONE known-safe prefix from a
 * closed-set SDK identifier. Never use it to push arbitrary prose past a
 * scrubber — see `CLAUDE/docs/guides/sentry-phi-determinations.md`, where
 * `extra.message` is deliberately left scrubbed for exactly that reason.
 */

/**
 * Strip the `auth/` prefix so a Firebase code survives scrubbing.
 *
 * Returns `'(none)'` for an absent code — an empty string is indistinguishable
 * from a missing key in a Sentry search, and "we saw no code" is itself a
 * finding (§3a.6: the verified-email gate arrived as `auth/unknown`, carrying
 * its only signal in the prose).
 *
 * @example scrubSafeAuthCode('auth/invalid-verification-code')
 *          // → 'invalid-verification-code'
 */
export function scrubSafeAuthCode(code: string | null | undefined): string {
  if (!code) return '(none)';
  return code.replace(/^auth\//, '');
}
